import type { QueryFunctionContext, QueryKey } from "@tanstack/react-query";
import {
  keepPreviousData,
  QueryClient,
  QueryClientProvider,
  QueryObserver,
  useQuery,
  useSuspenseInfiniteQuery,
  useSuspenseQuery,
} from "@tanstack/react-query";
import type { PropsWithChildren } from "react";
import { LogTags } from "./Logger";
import Exception, { ExceptionParams } from "./errors/Exception";

export type ExecutionContext = { page: number | null };

type ExecutionFn<Params, Output> = (
  params: Params,
  context: ExecutionContext,
) => Promise<Output>;

export type SuspendableOptions<Params, Output> = {
  cacheTime?: number;
  executionFn?: ExecutionFn<Params, Output>;
  pagination?: {
    mergeFn: (outputs: Array<Output>) => Output;
  };
  retryPolicy?: (attempt: number, error: unknown) => boolean;
};

type AnyOutput = any;

export type Suspending<Params, Output = void> = {
  params: Params;
  options: SuspendableOptions<Params, Output>;
};

export type OutputOf<S> =
  S extends Suspending<any, infer Output> ? Output : never;

export default class Suspender<Params> {
  constructor(
    private executionFn: ExecutionFn<Params, AnyOutput>,
    private hashFn: (params: Params) => string | QueryKey,
  ) {}

  public static queryClient: QueryClient = new QueryClient({
    defaultOptions: {
      queries: { staleTime: Infinity, retry: 0 },
    },
  });

  // Suspense
  build<Output>(
    params: Params,
    options: SuspendableOptions<Params, Output> = {},
  ): Suspending<Params, Output> {
    return { params, options };
  }

  buildStatic<Output>(value: Output): Suspending<Params, Output> {
    return {
      params: value as unknown as Params,
      options: { executionFn: async () => value },
    } as Suspending<Params, Output>;
  }

  // Build for TanStack

  private constructQueryKey(params: Params): QueryKey {
    const output = this.hashFn(params);
    if (typeof output === "string") return [output];
    else return output;
  }

  private constructQueryFn<S extends Suspending<Params, AnyOutput>>(s: S) {
    const { params, options } = s;
    const { executionFn } = options;
    const defFn = this.executionFn as ExecutionFn<Params, OutputOf<S>>;
    return async (): Promise<OutputOf<S>> => {
      try {
        const fn = executionFn || defFn;
        const output = await fn(params, { page: null });
        return output;
      } catch (error) {
        throw new SuspendableError(
          { cause: error, tags: { params: params as LogTags } },
          () => this.refresh(s),
        );
      }
    };
  }

  private constructPaginatedQueryFn<S extends Suspending<Params, AnyOutput>>(
    s: S,
  ) {
    const { params, options } = s;
    const { executionFn } = options;
    const defFn = this.executionFn as ExecutionFn<Params, OutputOf<S>>;
    return async (
      context: QueryFunctionContext<any, number>,
    ): Promise<OutputOf<S>> => {
      try {
        const fn = executionFn || defFn;
        const output = await fn(params, { page: context.pageParam });
        return output;
      } catch (error) {
        throw new SuspendableError(
          { cause: error, tags: { params: params as LogTags } },
          () => this.refresh(s),
        );
      }
    };
  }

  private constructStaleTime<S extends Suspending<Params, AnyOutput>>(s: S) {
    const cacheTime = s.options.cacheTime;
    return cacheTime === undefined ? Infinity : cacheTime;
  }

  onResult<S extends Suspending<Params, AnyOutput>>(
    s: S,
    fn: (output: OutputOf<S>) => void,
  ) {
    const queryKey = this.constructQueryKey(s.params);
    const queryFn = this.constructQueryFn(s);
    const staleTime = this.constructStaleTime(s);
    const observer = new QueryObserver<OutputOf<S>>(Suspender.queryClient, {
      queryKey,
      queryFn,
      staleTime,
      enabled: false,
    });
    const unsub = observer.subscribe((result) => {
      const data = result.data;
      if (data !== undefined) fn(data);
    });
    return unsub;
  }

  get<S extends Suspending<Params, AnyOutput>>(s: S): Promise<OutputOf<S>> {
    const queryKey = this.constructQueryKey(s.params);
    const queryFn = this.constructQueryFn(s);
    const staleTime = this.constructStaleTime(s);
    const promise = Suspender.queryClient.fetchQuery({
      queryKey,
      queryFn,
      staleTime,
    });
    return promise;
  }

  use<S extends Suspending<Params, AnyOutput>>(s: S): OutputOf<S> {
    const queryKey = this.constructQueryKey(s.params);
    const queryFn = this.constructQueryFn(s);
    const staleTime = this.constructStaleTime(s);
    const { data } = useSuspenseQuery({ queryKey, queryFn, staleTime });
    return data;
  }

  usePaginated<S extends Suspending<Params, AnyOutput>>(
    s: S,
  ): [OutputOf<S>, () => void] {
    const { options } = s;
    const { pagination } = options;
    if (!pagination) throw new Error("No pagination");
    const queryKey = this.constructQueryKey(s.params);
    const queryFn = this.constructPaginatedQueryFn(s);
    const staleTime = this.constructStaleTime(s);
    const { data, fetchNextPage } = useSuspenseInfiniteQuery({
      queryKey,
      queryFn,
      staleTime,
      initialPageParam: 1,
      getNextPageParam: (a, b, lastPageParams) => lastPageParams + 1,
    });
    const result = pagination.mergeFn(data.pages);
    return [result, fetchNextPage] as const;
  }

  useExecution<S extends Suspending<Params, AnyOutput>>(
    s: S,
    keep: boolean = false,
  ) {
    const queryKey = this.constructQueryKey(s.params);
    const queryFn = this.constructQueryFn(s);
    const staleTime = this.constructStaleTime(s);
    return useQuery({
      queryKey,
      queryFn,
      staleTime,
      placeholderData: keep ? keepPreviousData : undefined,
    });
  }

  set<S extends Suspending<Params, AnyOutput>>(
    suspendable: S,
    value: OutputOf<S>,
  ) {
    const { params } = suspendable;
    Suspender.queryClient.setQueryData(this.constructQueryKey(params), value);
  }

  refresh<S extends Suspending<Params, any>>(s: S) {
    const queryKey = this.constructQueryKey(s.params);
    void Suspender.queryClient.invalidateQueries({ queryKey });
  }
}

export class SuspendableError extends Exception {
  constructor(
    params: ExceptionParams,
    readonly retry: () => unknown,
  ) {
    super(params);
  }
}

export type ExecutionOptions = {
  noisy?: boolean;
  preserve?: boolean;
};

export function SuspenderProvider(props: PropsWithChildren) {
  return QueryClientProvider({
    client: Suspender.queryClient,
    children: props.children,
  });
}
